import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Button,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Drawer,
    OutlinedInput,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
} from '@material-ui/core';
import {
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    WhatsApp as WhatsAppIcon,
} from '@material-ui/icons';
import { routes } from '../../constants.js/misc';
import useStyles from './OrderInfoPageMobile.styles';
import useDrawerStyles from '../OrderPageMobile/OrderPageMobile.styles';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { Mixpanel } from '../../constants.js/mixpanel';
import { getDynamicFormInfo, getOrderService, updateOrder } from '../../services/orders';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { setOrderToEdit, setPageState, setRowsPerPageState } from '../../modules/orders';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { errorToast, infoToast, successToast } from '../../constants.js/misc';
import { paymentStatusConfigs, shipmentStatusConfigs } from '../../constants.js/orders';
import moment from 'moment';
import { getUserStoresService } from '../../services/store';

export const OrderInfoPageMobile = ({ orderToEdit, setOrderToEdit, storeId }) => {
    const { id, orderId } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const history = useHistory();
    const classes = useStyles();
    const drawerClasses = useDrawerStyles();

    const [gettingOrder, setGettingOrder] = useState(true);
    const [orderData, setOrderData] = useState({});
    const [userStores, setUserStores] = useState([]);
    const [formInfo, setFormInfo] = useState([]);
    const [isPaymentStatusDrawerOpen, setIsPaymentStatusDrawerOpen] = useState(false);
    const [isShipmentStatusDrawerOpen, setIsShipmentStatusDrawerOpen] = useState(false);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('');
    const [selectedShipmentStatus, setSelectedShipmentStatus] = useState('');

    const debounceRef = useRef(null);

    const formatCurrency = (amount) => {
        if (!amount) return 0;
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const handleEditOrder = () => {
        // Handle edit order
        console.log('Edit order');
    };

    const handleChangeOrderStatus = () => {
        setIsPaymentStatusDrawerOpen(true);
    };

    const handleChangeShippingStatus = () => {
        setIsShipmentStatusDrawerOpen(true);
    };

    const handlePaymentStatusChange = (event) => {
        const value = Number(event.target.value); // radios return string, so convert
        const newPaymentStatus = Object.values(paymentStatusConfigs).find(
            (config) => config.value === value
        );

        setSelectedPaymentStatus(value);
        setOrderData((prev) => ({
            ...prev,
            order_status: { id: newPaymentStatus?.value, name: newPaymentStatus?.label },
            order_status_id: newPaymentStatus?.value,
        }));
    };

    const handleShipmentStatusChange = (event) => {
        const value = Number(event.target.value);
        const newShipmentStatus = Object.values(shipmentStatusConfigs).find(
            (config) => config.value === value
        );

        setSelectedShipmentStatus(value);
        setOrderData((prev) => ({
            ...prev,
            shipping_order_status: { id: newShipmentStatus?.value, name: newShipmentStatus?.label },
            shipping_order_status_id: newShipmentStatus?.value,
        }));
    };

    const handleDateChange = (e) => {
        const value = e.target.value;
        if (moment(value).isValid()) {
            const isoString = moment(value).toISOString();

            setOrderData((prev) => ({
                ...prev,
                created_at: isoString,
            }));

            // clear previous timer if still running
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            debounceRef.current = setTimeout(() => {
                updateOrder(storeId, orderData.id, {
                    ...orderData,
                    created_at: isoString,
                }).then(() => {
                    setTimeout(() => {
                        enqueueSnackbar(t('OrderDetail.submitSuccess'), successToast);
                    }, 1500);
                });
            }, 1000);
        }
    };

    const handleOnCommentsChange = (e) => {
        const { value } = e.target;

        // update local state right away
        setOrderData((prev) => ({
            ...prev,
            comments: value,
        }));

        // clear previous timer if still running
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // set new debounce timer
        debounceRef.current = setTimeout(() => {
            updateOrder(storeId, orderData.id, { ...orderData, comments: value }).then(() => {
                setTimeout(() => {
                    enqueueSnackbar(t('OrderDetail.submitSuccess'), successToast);
                }, 1500);
            });
        }, 1000);
    };

    const handleStoreChange = (e) => {
        const storeId = e.target.value;
        const selectedStore = userStores.find((store) => store.id === storeId);

        // Update local state
        setOrderData((prev) => ({
            ...prev,
            user_store_id: storeId,
        }));

        // clear previous timer if still running
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        // Call backend
        debounceRef.current = setTimeout(() => {
            updateOrder(storeId, orderData.id, {
                user_store_id: storeId,
            }).then(() => {
                setTimeout(() => {
                    enqueueSnackbar(t('OrderDetail.submitSuccess'), successToast);
                }, 1500);
            });
        }, 1000);
    };

    const handleCalculateSubtotal = (orderDetails, isRawCalculation = false) => {
        if (!orderDetails || orderDetails.length === 0) return 0;

        const subtotal = orderDetails.reduce((acc, item) => {
            const itemPrice = item.price * item.quantity;
            return acc + itemPrice;
        }, 0);

        if (isRawCalculation) return subtotal;

        return formatCurrency(subtotal);
    };

    const handleCalculateTotalWithShipping = (orderDetails, shippingCost) => {
        const subtotal = handleCalculateSubtotal(orderDetails, true);
        const total = subtotal + shippingCost;
        return formatCurrency(total);
    };

    const handleStatusSave = () => {
        const payload = {
            ...orderData,
            user_store_id: orderData.user_store_id,
        };

        if (selectedPaymentStatus) {
            const paymentConfig = Object.values(paymentStatusConfigs).find(
                (c) => c.value === selectedPaymentStatus
            );
            if (paymentConfig) {
                payload.order_status_id = paymentConfig.value;
            }
        }

        if (selectedShipmentStatus) {
            const shipmentConfig = Object.values(shipmentStatusConfigs).find(
                (c) => c.value === selectedShipmentStatus
            );
            if (shipmentConfig) {
                payload.shipping_order_status_id = shipmentConfig.value;
            }
        }

        // Only call update if something actually changed
        if (payload.order_status_id || payload.shipping_order_status_id) {
            updateOrder(storeId, orderData.id, payload)
                .then(() => {
                    setTimeout(() => {
                        enqueueSnackbar(t('OrderDetail.submitSuccess'), successToast);
                    }, 1500);
                })
                .catch(() => {
                    setTimeout(() => {
                        enqueueSnackbar(t('OrderDetail.submitError'), errorToast);
                    }, 1500);
                });
        }

        setIsPaymentStatusDrawerOpen(false);
        setIsShipmentStatusDrawerOpen(false);
    };

    /**
     * Get the product data if an id is provided in the url
     */
    useEffect(() => {
        if (id) {
            setGettingOrder(true);
            /**
             * If the variable is not set it means the user got to the page using
             * only the url, get the store data from the api to show the info
             */
            getOrderService(storeId, id)
                .then(({ data }) => {
                    setGettingOrder(false);
                    setOrderData(data);
                    Mixpanel.track('Visitar resumen del pedido', {
                        'Canal de venta': data.user_store ? data.user_store.name : '',
                        productos: data.order_details,
                        pedido: data.sequence,
                        'ID pedido': id,
                    });
                })
                .catch(() => {
                    setGettingOrder(false);
                    enqueueSnackbar(t('OrderDetail.dataError'), errorToast);
                    history.push(routes.ordersPage);
                });

            getDynamicFormInfo(storeId, id)
                .then(({ data }) => {
                    setFormInfo(data);
                })
                .catch((err) => console.log(err));
            getUserStoresService(storeId).then(({ data }) => setUserStores(data));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // HOCs - Status chip rendering functions
    const getPaymentStatusChip = (value) => {
        const config = Object.values(paymentStatusConfigs).find((c) => c.value === value);
        if (config) {
            return (
                <Chip
                    label={config.label}
                    className={`${drawerClasses.statusChip} ${drawerClasses[config.class]}`}
                    size="small"
                />
            );
        }
    };

    const getShipmentStatusChip = (value) => {
        const config = Object.values(shipmentStatusConfigs).find((c) => c.value === value);
        if (config) {
            return (
                <Chip
                    label={config.label}
                    className={`${drawerClasses.statusChip} ${drawerClasses[config.class]}`}
                    size="small"
                />
            );
        }
    };

    const renderPaymentStatusOptions = () => {
        return Object.entries(paymentStatusConfigs).map(([key, config]) => (
            <FormControlLabel
                key={config.value}
                value={config.value}
                control={<Radio color="primary" />}
                label={
                    <div className={drawerClasses.statusOptionContent}>
                        {getPaymentStatusChip(config.value)}
                        <Typography variant="body2" color="textSecondary">
                            {config.description}
                        </Typography>
                    </div>
                }
                className={drawerClasses.statusOption}
            />
        ));
    };

    const renderShipmentStatusOptions = () => {
        return Object.entries(shipmentStatusConfigs).map(([key, config]) => (
            <FormControlLabel
                key={config.value}
                value={config.value}
                control={<Radio color="primary" />}
                label={
                    <div className={drawerClasses.statusOptionContent}>
                        {getShipmentStatusChip(config.value)}
                        <Typography variant="body2" color="textSecondary">
                            {config.description}
                        </Typography>
                    </div>
                }
                className={drawerClasses.statusOption}
            />
        ));
    };

    if (gettingOrder) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    textAlign: 'center',
                    padding: '16px',
                }}
            >
                <CircularProgress style={{ marginBottom: '20px', color: '#9EE870' }} />
                <Typography variant="h6" style={{ color: '#173300' }}>
                    Cargando los datos del pedido...
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Por favor espera un momento mientras preparamos la información.
                </Typography>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            {/* Header */}
            <AppBar position="sticky" className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                    <Link to={routes.ordersPage} className={classes.headerLeft}>
                        <IconButton size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography className={classes.breadcrumb}>Pedidos</Typography>
                    </Link>
                    <Typography className={classes.title}>Pedido</Typography>
                    <Button className={classes.editButton} onClick={handleEditOrder}>
                        Editar pedido
                    </Button>
                </Toolbar>

                <div className={classes.statusContainer}>
                    <div className={classes.statusChips}>
                        {orderData?.order_status_id &&
                            getPaymentStatusChip(orderData?.order_status_id)}
                        {orderData?.shipping_order_status_id &&
                            getShipmentStatusChip(orderData?.shipping_order_status_id)}
                    </div>
                    <IconButton size="small">
                        <VisibilityIcon />
                    </IconButton>
                </div>
            </AppBar>

            {/* Main Content */}
            <div className={classes.mainContent}>
                {/* Order Status Section */}
                <Card className={classes.section}>
                    <CardContent>
                        <Typography className={classes.sectionTitle}>Estado del pedido</Typography>
                        <Typography className={classes.infoValue} style={{ marginBottom: 16 }}>
                            {orderData?.order_status?.name}
                        </Typography>
                        <Button className={classes.ctaButton} onClick={handleChangeOrderStatus}>
                            Cambiar estado del pedido
                        </Button>
                    </CardContent>
                </Card>

                {/* Payment Info Section */}
                <Card className={classes.section}>
                    <CardContent>
                        <Typography className={classes.sectionTitle}>
                            Información de pago
                        </Typography>
                        {/* <Alert severity="error" className={classes.paymentAlert}>
                            El cliente no ha realizado el pago
                        </Alert> */}
                        <a
                            href={`https://wa.me/${orderData?.customer?.phone_number ?? ''}`}
                            target="_blank"
                            className={classes.whatsappButton}
                            rel="noopener noreferrer"
                        >
                            <WhatsAppIcon style={{ color: '#25D366' }} />
                            <span>Enviar enlace de pago</span>
                        </a>
                    </CardContent>
                </Card>

                {/* Customer Section */}
                <Card className={classes.section}>
                    <CardContent>
                        <Typography className={classes.sectionTitle}>Cliente</Typography>
                        <div>
                            <div className={classes.infoRow}>
                                <span className={classes.infoLabel}>Nombre</span>
                                <span className={classes.infoValue}>
                                    {orderData?.customer?.first_name ?? ''}
                                </span>
                            </div>
                            <div className={classes.infoRow}>
                                <span className={classes.infoLabel}>Email</span>
                                <span className={classes.infoValue}>
                                    {orderData?.customer?.email ?? ''}
                                </span>
                            </div>
                            <div className={classes.infoRow}>
                                <span className={classes.infoLabel}>Teléfono</span>
                                <span className={classes.infoValue}>
                                    {orderData?.customer?.phone_number ?? ''}
                                </span>
                            </div>
                            <div className={classes.infoRow}>
                                <span className={classes.infoLabel}>Identificación</span>
                                <span className={classes.infoValue}>
                                    {orderData?.customer?.document_type ?? ''}{' '}
                                    {orderData?.customer?.dni ?? ''}
                                </span>
                            </div>
                            {orderData?.customer?.phone_number && (
                                <div className={classes.infoRow}>
                                    <span className={classes.infoLabel}>WhatsApp</span>
                                    <a
                                        href={`https://wa.me/${orderData.customer.phone_number}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <WhatsAppIcon style={{ color: '#25D366' }} />
                                    </a>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Shipping Info Section */}
                <Card className={classes.section}>
                    <CardContent>
                        <Typography className={classes.sectionTitle}>Datos de envío</Typography>
                        <div style={{ marginBottom: 16 }}>
                            <div className={classes.infoRow}>
                                <span className={classes.infoLabel}>Teléfono</span>
                                <span className={classes.infoValue}>
                                    {orderData?.customer?.phone_number ?? ''}
                                </span>
                            </div>
                            <div className={classes.infoRow}>
                                <span className={classes.infoLabel}>Nombre</span>
                                <span className={classes.infoValue}>
                                    {orderData?.customer?.first_name ??
                                        '' + ' ' + orderData?.customer?.last_name ??
                                        ''}
                                </span>
                            </div>
                            <div className={classes.infoRow} style={{ alignItems: 'flex-start' }}>
                                <span className={classes.infoLabel}>Dirección</span>
                                <div className={classes.addressValue}>
                                    {orderData?.customer?.address.split('\n').map((line, index) => (
                                        <div key={index}>{line}</div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className={classes.divider} />

                        <div style={{ marginBottom: 16 }}>
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 500, color: '#173300', marginBottom: 8 }}
                            >
                                Estado del envío
                            </Typography>
                            <Typography className={classes.infoLabel} style={{ marginBottom: 16 }}>
                                {
                                    Object.values(shipmentStatusConfigs).find(
                                        (c) => c.value === orderData?.shipping_order_status_id
                                    )?.label
                                }
                            </Typography>
                        </div>

                        <Button className={classes.ctaButton} onClick={handleChangeShippingStatus}>
                            Cambiar estado del envío
                        </Button>
                    </CardContent>
                </Card>

                <Card className={classes.section}>
                    <Typography variant="h6">Fecha y hora del pedido</Typography>
                    <TextField
                        type="datetime-local"
                        value={moment(orderData.created_at).format('YYYY-MM-DDTHH:mm')}
                        onChange={handleDateChange}
                        variant="outlined"
                        fullWidth
                        style={{ marginTop: 8 }}
                    />
                </Card>
                <Card className={classes.section}>
                    <Typography variant="h6">Canal de venta</Typography>
                    <FormControl variant="outlined" fullWidth style={{ marginTop: 16 }}>
                        <InputLabel id="store-select-label">Canal de venta</InputLabel>
                        <Select
                            value={orderData?.user_store?.id ?? ''}
                            onChange={handleStoreChange}
                            label="Canal de venta"
                            variant="outlined"
                        >
                            {userStores.map((store) => (
                                <MenuItem key={store.id} value={store.id}>
                                    {store.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Card>

                {/* Order Details Section */}
                <Card className={classes.section}>
                    <CardContent>
                        <div className={classes.orderMeta}>
                            <Typography className={classes.sectionTitle}>
                                Detalles del pedido
                            </Typography>
                            <Typography className={classes.orderDate}>{orderData?.date}</Typography>
                            <Typography className={classes.salesChannel}>
                                Canal de venta:{' '}
                                <a href={`tel:${orderData.salesChannel}`}>
                                    {orderData?.user_store?.name}
                                </a>
                            </Typography>
                        </div>

                        <TableContainer>
                            <Table className={classes.productTable}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Producto</TableCell>
                                        <TableCell align="center">Cant.</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orderData?.order_details?.map((item) => (
                                        <TableRow key={item.id} className={classes.productRow}>
                                            <TableCell>
                                                <div className={classes.productInfo}>
                                                    <Avatar
                                                        className={classes.productImage}
                                                        variant="rounded"
                                                        src={
                                                            item?.product?.product_images?.[0] || ''
                                                        }
                                                    />
                                                    <div className={classes.productDetails}>
                                                        <div className="product-name">
                                                            {item?.product.name}
                                                        </div>
                                                        {/* <div className="product-variant">
                                                            Variación: {item?.variant}
                                                        </div> */}
                                                        <div className="product-sku">
                                                            SKU: {item?.product.sku}
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell align="center">{item?.quantity}</TableCell>
                                            <TableCell align="right">
                                                {formatCurrency(item.price * item.quantity)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <div className={classes.totalSection}>
                            <div className={classes.totalRow}>
                                <span className={classes.infoLabel}>Subtotal</span>
                                <span className={classes.infoValue}>
                                    {handleCalculateSubtotal(orderData?.order_details)}
                                </span>
                            </div>
                            <div className={classes.totalRow}>
                                <span className={classes.infoLabel}>Costos de envío</span>
                                <span className={classes.infoValue}>
                                    {formatCurrency(orderData?.shipping_cost)}
                                </span>
                            </div>
                            <div className={`${classes.totalRow} total-final`}>
                                <span style={{ color: '#173300' }}>Total</span>
                                <span style={{ color: '#173300' }}>
                                    {handleCalculateTotalWithShipping(
                                        orderData?.order_details,
                                        orderData?.shipping_cost
                                    )}
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Order Comments Section */}
                <Card className={classes.section}>
                    <CardContent>
                        <Typography className={classes.sectionTitle}>
                            Comentarios del pedido
                        </Typography>
                        <OutlinedInput
                            className={classes.commentsBox}
                            value={orderData?.comments ?? ''}
                            onChange={handleOnCommentsChange}
                        />
                    </CardContent>
                </Card>

                {/* Custom Form Section */}
                <Card className={classes.section}>
                    <CardContent>
                        <Typography className={classes.sectionTitle}>
                            Respuestas del formulario personalizado
                        </Typography>
                        <div>
                            {formInfo?.map((form) => (
                                <div className={classes.infoRow}>
                                    <span className={classes.infoLabel}>{form.title}</span>
                                    <span className={classes.infoValue}>{form.response}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
            {/* Payment Status Change Dialog */}
            <Drawer
                anchor="bottom"
                open={isPaymentStatusDrawerOpen}
                onClose={() => setIsPaymentStatusDrawerOpen(false)}
                PaperProps={{ style: { borderRadius: '16px 16px 0px 0px' } }}
            >
                <div className={drawerClasses.dialogTitle}>
                    <Typography variant="h6">Cambiar estado</Typography>
                    <IconButton onClick={() => setIsPaymentStatusDrawerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div>
                    <RadioGroup value={selectedPaymentStatus} onChange={handlePaymentStatusChange}>
                        {renderPaymentStatusOptions()}
                    </RadioGroup>
                </div>
                <div
                    style={{
                        padding: '16px 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Button
                        onClick={() => setIsPaymentStatusDrawerOpen(false)}
                        style={{ color: '#4A4A4A', flex: 1 }}
                        fullWidth
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleStatusSave}
                        style={{ backgroundColor: '#9EE870', color: 'white', flex: 1 }}
                        fullWidth
                    >
                        Guardar
                    </Button>
                </div>
            </Drawer>

            {/* Shipment Status Change Dialog */}
            <Drawer
                anchor="bottom"
                open={isShipmentStatusDrawerOpen}
                onClose={() => setIsShipmentStatusDrawerOpen(false)}
                PaperProps={{ style: { borderRadius: '16px 16px 0px 0px' } }}
            >
                <div className={drawerClasses.dialogTitle}>
                    <Typography variant="h6">Cambiar estado</Typography>
                    <IconButton onClick={() => setIsShipmentStatusDrawerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div>
                    <RadioGroup
                        value={selectedShipmentStatus}
                        onChange={handleShipmentStatusChange}
                    >
                        {renderShipmentStatusOptions()}
                    </RadioGroup>
                </div>
                <div
                    style={{
                        padding: '16px 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Button
                        onClick={() => setIsShipmentStatusDrawerOpen(false)}
                        style={{ color: '#4A4A4A', flex: 1 }}
                        fullWidth
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleStatusSave}
                        style={{ backgroundColor: '#9EE870', color: 'white', flex: 1 }}
                        fullWidth
                    >
                        Guardar
                    </Button>
                </div>
            </Drawer>
        </div>
    );
};

const mapStateToProps = ({ auth, orders, common }) => ({
    storeId: auth.storeId,
    orderToEdit: orders.orderToEdit,
    storeData: common.storeData,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setOrderToEdit,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(OrderInfoPageMobile);
